import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  SxProps,
  TextField,
  Typography,
} from "@mui/material";
import { ForwardedRef, PropsWithChildren, ReactNode, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

import { useTemplateData } from "../../../../utils/TemplateDataProvider";
import { useTemplateHelperInfo } from "../../../../utils/TemplateHelperInfoProvider";
import { useDevice } from "../../../../utils/DeviceProvider";
import { NewTemplateHelper, TemplateHelper } from "../../../../utils/types";
import { useLocale } from "../../../../locales/LocaleProvider";
import { useMutation } from "react-query";
import { useErrorHandler } from "../../../../hooks/useErrorHandler";
import { useIntl } from "react-intl";
import { useApi } from "../../../../utils/ApiProvider";
import { useSnackbar } from "notistack";

interface ElementFrameProps
  extends PropsWithChildren<{
    label: string;
    sx?: SxProps;
    disabled?: boolean;
  }> {}

const ElementFrame = forwardRef(({ children, label, sx, disabled }: ElementFrameProps, ref: ForwardedRef<ReactNode>) => {
  const [open, setOpen] = useState(false);
  const [openReader, setOpenReader] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [keyDisabled, setKeyDisabled] = useState(false);

  const formSaveModeRef = useRef<"create" | "edit" | null>(null);

  const {
    device: { unitDetails },
  } = useDevice();
  const { type, key } = useTemplateData();
  const { templateHelperInfo: helper, refetchHelperInfo, editEnabled } = useTemplateHelperInfo();
  const { locale } = useLocale();
  const { formatMessage } = useIntl();
  const api = useApi();
  const { closeSnackbar } = useSnackbar();

  const { errorHandler: onError } = useErrorHandler<TemplateHelper>([
    [{ status: 400 }, () => formatMessage({ defaultMessage: "Configuration helper already exists with rule like this." })],
    [{ status: 401, type: "template-helpers" }, () => formatMessage({ defaultMessage: "You have no rights for remote file management." })],
    [{ status: 404 }, () => formatMessage({ defaultMessage: "Configuration helper not found." })],
    // [
    //   { status: 400, matcher: ({ error, value }) => error?.message === `File already exists with this name ${value.filename}.` },
    //   () => formatMessage({ defaultMessage: "Remote file already exist with this name." }),
    // ],
  ]);

  const onSuccess = useCallback(() => {
    handleClose();
    refetchHelperInfo();
  }, [refetchHelperInfo]);

  const createQuery = useCallback(
    (file: NewTemplateHelper) => {
      closeSnackbar();
      return api(`/template-helpers`, { method: "post", body: JSON.stringify(file) });
    },
    [api, closeSnackbar]
  );
  const saveQuery = useCallback(
    (file: TemplateHelper) => {
      closeSnackbar();
      return api(`/template-helpers/${file.id}`, { method: "post", body: JSON.stringify(file) });
    },
    [api, closeSnackbar]
  );
  const deleteQuery = useCallback(
    (id: number) => {
      closeSnackbar();
      return api(`/template-helpers/${id}`, { method: "delete" });
    },
    [api, closeSnackbar]
  );

  const { mutate: create, isLoading: createIsLoading } = useMutation({ mutationFn: createQuery, onError, onSuccess });
  const { mutate: edit, isLoading: saveIsLoading } = useMutation({ mutationFn: saveQuery, onError, onSuccess });
  const { mutate: deleteQ, isLoading: deleteIsLoading } = useMutation({ mutationFn: deleteQuery, onError, onSuccess });

  const loading = useMemo(() => createIsLoading || saveIsLoading || deleteIsLoading, [createIsLoading, saveIsLoading, deleteIsLoading]);

  const defaultValues = useMemo<TemplateHelper | NewTemplateHelper | undefined>(() => {
    if (!unitDetails) {
      return;
    }
    const def: NewTemplateHelper = { key, keyRule: key ? "equals" : "all", type, info: "", hwtype: "", lang: locale };

    const infoByType = helper[locale]?.[type];
    if (!infoByType) {
      return def;
    }

    const info: TemplateHelper[] = infoByType
      .filter(
        ({ keyRule, key: k }) => keyRule === "all" || (keyRule === "equals" && `${key}` === k) || (keyRule === "startsWith" && k && key && key.startsWith(k))
      )
      .map((rule) => ({ ...rule, order: rule.keyRule === "equals" ? 0 : rule.keyRule === "startsWith" ? 1 : 2 }))
      .sort(({ order: a }, { order: b }) => a - b)
      .map(({ id, info, key, keyRule, hwtype }) => ({ id, info, key, keyRule, type, hwtype, lang: locale }));
    return info[0] ?? def;
  }, [unitDetails, key, type, locale, helper]);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleClickOpenReader = () => {
    setOpenReader(true);
  };

  const handleCloseReader = () => {
    setOpenReader(false);
  };

  const handleOpenConfirm = () => {
    setOpenConfirm(true);
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
  };

  const ruleChanged = useCallback((e: SelectChangeEvent) => {
    setKeyDisabled(e.target.value === "all");
  }, []);

  const handleCreate = useCallback(() => {
    formSaveModeRef.current = "create";
  }, []);

  const handleEdit = useCallback(() => {
    formSaveModeRef.current = "edit";
  }, []);

  const handleDelete = useCallback(() => {
    handleCloseConfirm();
    deleteQ((defaultValues as TemplateHelper).id);
  }, [defaultValues, deleteQ]);

  useEffect(() => {
    setKeyDisabled(!key);
  }, [key]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", ...sx }} ref={ref}>
      <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
        {label && <Typography variant="subtitle2" color={disabled ? "text.disabled" : "inherit"}>{label}</Typography>}
        {editEnabled && (
          <IconButton size="small" sx={{ color: (theme) => theme.palette.warning.main }} onClick={handleClickOpen}>
            <EditOutlinedIcon fontSize="inherit" />
          </IconButton>
        )}
        {defaultValues?.info && (
          <IconButton size="small" sx={{ color: (theme) => theme.palette.secondary.light }} onClick={handleClickOpenReader}>
            <InfoOutlinedIcon fontSize="inherit" />
          </IconButton>
        )}
      </Box>

      {children}

      {defaultValues ? (
        <Dialog
          open={open}
          onClose={handleClose}
          maxWidth="xl"
          PaperProps={{
            component: "form",
            onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
              const saveMode = formSaveModeRef.current;
              event.preventDefault();
              event.stopPropagation();
              const formData = new FormData(event.currentTarget);
              const { type, keyRule, key, lang, hwtype, info } = Object.fromEntries((formData as any).entries());
              if (saveMode === "create") {
                create({ type, keyRule, key, lang, hwtype: hwtype || undefined, info });
              }
              if (saveMode === "edit") {
                edit({ id: (defaultValues as TemplateHelper).id, type, keyRule, key, lang, hwtype: hwtype || undefined, info });
              }
            },
          }}
        >
          <DialogTitle sx={{ backgroundColor: (theme) => theme.palette.secondary.main, color: (theme) => theme.palette.secondary.contrastText }}>
            {formatMessage({ defaultMessage: "Edit configuration helper" })}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              <strong>{formatMessage({ defaultMessage: "Selected item:" })}</strong> {label}
            </DialogContentText>
            <DialogContentText>
              <strong>{formatMessage({ defaultMessage: "Type:" })}</strong> <code>{type}</code>
            </DialogContentText>
            <DialogContentText>
              <strong>{formatMessage({ defaultMessage: "Key:" })}</strong> {key === undefined ? <em>Nincs megadva</em> : <code>{key}</code>}
            </DialogContentText>
            <DialogContentText>
              <strong>{formatMessage({ defaultMessage: "Hardware type:" })}</strong> {unitDetails?.hwtype}
            </DialogContentText>
            <DialogContentText variant="h6" sx={{ mt: 2 }}>
              {formatMessage({ defaultMessage: "Rule" })}
            </DialogContentText>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={4} lg={3}>
                <TextField name="type" required label="Típus" fullWidth variant="filled" size="small" sx={{ m: 0 }} defaultValue={defaultValues.type} />
              </Grid>
              <Grid item xs={12} sm={4} lg={3}>
                <FormControl fullWidth required variant="filled" size="small" sx={{ m: 0 }}>
                  <InputLabel>{formatMessage({ defaultMessage: "Key rule" })}</InputLabel>
                  <Select name="keyRule" defaultValue={defaultValues.keyRule} onChange={ruleChanged}>
                    <MenuItem value="all">
                      <em>{formatMessage({ defaultMessage: "All keys" })}</em>
                    </MenuItem>
                    <MenuItem value="equals">{formatMessage({ defaultMessage: "Equals" })}</MenuItem>
                    <MenuItem value="startsWith">{formatMessage({ defaultMessage: "Starts with" })}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4} lg={3}>
                <TextField
                  name="key"
                  label="Kulcs"
                  fullWidth
                  variant="filled"
                  size="small"
                  sx={{ m: 0 }}
                  defaultValue={defaultValues.key}
                  InputLabelProps={{ shrink: true }}
                  disabled={keyDisabled}
                />
              </Grid>
              <Grid item xs={12} sm={4} lg={3}>
                <FormControl fullWidth required variant="filled" size="small" sx={{ m: 0 }}>
                  <InputLabel>{formatMessage({defaultMessage: "Locale"})}</InputLabel>
                  {/* TODO: nyelvek dinamikusan */}
                  <Select name="lang" defaultValue={defaultValues.lang}>
                    <MenuItem value="hu">Magyar</MenuItem>
                    <MenuItem value="en">English</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4} lg={3}>
                <TextField
                  name="hwtype"
                  label="Hardvertípus"
                  fullWidth
                  variant="filled"
                  size="small"
                  sx={{ m: 0 }}
                  defaultValue={defaultValues.hwtype}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  autoFocus
                  name="info"
                  required
                  label="Leírás"
                  fullWidth
                  variant="filled"
                  size="small"
                  sx={{ m: 0 }}
                  defaultValue={defaultValues.info}
                  InputLabelProps={{ shrink: true }}
                  multiline
                  minRows={5}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            {(defaultValues as TemplateHelper).id && (
              <Box sx={{ flex: 1 }}>
                <Button onClick={handleOpenConfirm} color="error" disabled={loading}>
                  {formatMessage({ defaultMessage: "Delete" })}
                </Button>
              </Box>
            )}
            <Button onClick={handleClose} color="inherit">
              {formatMessage({ defaultMessage: "Cancel" })}
            </Button>
            <Button type="submit" onClick={handleCreate} color="secondary" disabled={loading}>
              {formatMessage({ defaultMessage: "Save as new rule" })}
            </Button>
            <Button type="submit" onClick={handleEdit} color="secondary" disabled={loading || !(defaultValues as TemplateHelper).id}>
              {formatMessage({ defaultMessage: "Save" })}
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}
      {defaultValues?.info ? (
        <Dialog open={openReader} onClose={handleCloseReader} maxWidth="md">
          <DialogTitle sx={{ backgroundColor: (theme) => theme.palette.secondary.main, color: (theme) => theme.palette.secondary.contrastText }}>
            {formatMessage({ defaultMessage: "Configuration helper" })}
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ whiteSpace: "pre-line" }}>{defaultValues.info}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseReader} autoFocus color="secondary">
              {formatMessage({ defaultMessage: "OK" })}
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}
      <Dialog open={openConfirm} onClose={handleCloseConfirm} maxWidth="md">
        <DialogTitle sx={{ backgroundColor: (theme) => theme.palette.error.main, color: (theme) => theme.palette.error.contrastText }}>
          {formatMessage({ defaultMessage: "Delete configuration helper" })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: "pre-line" }}>
            {formatMessage({ defaultMessage: "Are you sure to delete this configuration helper?" })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm} color="inherit">
            {formatMessage({ defaultMessage: "Cancel" })}
          </Button>
          <Button onClick={handleDelete} color="error">
            {formatMessage({ defaultMessage: "Delete" })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default ElementFrame;
